import { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../../components/Layout';
import styles from './TypesenseSync.module.scss';
import { useGetJobs } from '../../api/hooks/useGetJobs';
import type { JobDetailsResponse } from '../../api/contracts/JobsResponse';
import { StartJobDialog } from './StartJobDialog';
import { useCreateJob } from '../../api/hooks/useCreateJob';
import { JobsListSkeleton } from './JobsListSkeleton';
import { formatDate } from '../../utils/formatDate';
// import { Notification } from '../../components/Notification';

const calculateDuration = (createdAt: string, updatedAt: string) => {
  const start = new Date(createdAt).getTime();
  const end = new Date(updatedAt).getTime();
  const durationMs = end - start;
  const durationMinutes = Math.max(1, Math.round(durationMs / (1000 * 60)));
  return `${durationMinutes} min`;
};

export const TypesenseSync = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data,
    isLoading,
    mutate: refreshJobs,
  } = useGetJobs(50, 'typesense_sync', {
    refreshInterval: 10000,
  });

  const { trigger: createJob } = useCreateJob();

  const [jobs, setJobs] = useState<JobDetailsResponse[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  // const [notificationOpen, setNotificationOpen] = useState(false);
  // const [notificationMessage, setNotificationMessage] = useState('');
  //   const [notificationSeverity, setNotificationSeverity] = useState<
  //     'success' | 'error'
  //   >('success');

  useEffect(() => {
    if (data) {
      setJobs(data.jobs);
    }
  }, [data]);

  const handleNewJob = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleStartJob = async (limit?: number) => {
    await createJob({
      body: {
        jobType: 'typesense_sync',
        limit,
      },
    });
    refreshJobs();
    handleCloseDialog();
  };

  //   const handleCloseNotification = () => {
  //     setNotificationOpen(false);
  //   };

  return (
    <Layout>
      <div className={styles.header}>
        <Typography variant="h4" component="h1">
          Typesense Sync - Jobs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewJob}
          size={isMobile ? 'small' : 'medium'}
        >
          New Job
        </Button>
      </div>

      <div className={styles.jobsList}>
        {isLoading ? (
          <JobsListSkeleton />
        ) : (
          <List>
            {jobs.map((job) => (
              <ListItem key={job.jobId} className={styles.jobItem}>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 1 : 2,
                        alignItems: isMobile ? 'flex-start' : 'center',
                        mb: isMobile ? 1 : 0,
                      }}
                    >
                      {job.status}
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 0.5 : 2,
                        alignItems: isMobile ? 'flex-start' : 'center',
                      }}
                    >
                      <span className={styles.jobMeta}>
                        Started: {formatDate(job.createdAt)}
                      </span>
                      <span className={styles.jobMeta}>
                        Duration:{' '}
                        {calculateDuration(
                          job.createdAt,
                          job.updatedAt || job.createdAt
                        )}
                      </span>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <StartJobDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onStart={handleStartJob}
      />

      {/* <Notification
        open={notificationOpen}
        message={notificationMessage}
        severity={notificationSeverity}
        onClose={handleCloseNotification}
      /> */}
    </Layout>
  );
};
